// Visual proxies only. Shopify's native picker owns variant selection, prices and media.
const portraitNormalize = (value) => String(value ?? '').trim().toLowerCase();

class PersonalizedPortraitOptions extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = new AbortController();
    const { signal } = this.controller;
    const details = this.closest('.personalized-product-details--portrait');
    const hideFailedExample = image => {
      if (!image?.matches('.portrait-design__card img')) return;
      image.hidden = true;
      if (image.nextElementSibling?.tagName === 'SMALL') image.nextElementSibling.hidden = true;
    };
    details?.addEventListener('error', event => hideFailedExample(event.target), { capture: true, signal });
    details?.querySelectorAll('.portrait-design__card img').forEach(image => {
      if (image.complete && !image.naturalWidth) hideFailedExample(image);
    });
    this.picker = this.closest('.personalized-product-details--portrait')?.querySelector('variant-picker');
    if (!this.picker || this.picker.dataset.productId !== this.dataset.productId) return;
    this.groups = new Set();
    try { this.variantMedia = JSON.parse(details.querySelector('[data-portrait-variant-media]')?.textContent || '{}'); } catch { this.variantMedia = {}; }
    this.orientationRadios = () => [...this.picker.querySelectorAll('input[data-option-name]')].filter(input => portraitNormalize(input.dataset.optionName) === 'orientation');
    if (this.orientationRadios().length && !this.querySelector('[data-portrait-orientation="Horizontal"]')) {
      const sizeGroup = this.querySelector('[data-portrait-kind="size"]');
      const cards = [...sizeGroup.querySelectorAll('[data-portrait-value]')];
      for (const card of cards) {
        card.dataset.portraitOrientation = 'Vertical';
        const horizontal = card.cloneNode(true); horizontal.dataset.portraitOrientation = 'Horizontal';
        const dimensions = this.dimensions(card.dataset.portraitValue);
        card.querySelector('.portrait-options__label').textContent = `${dimensions[0]}″ × ${dimensions[1]}″ (Vertical)`;
        horizontal.querySelector('.portrait-options__label').textContent = `${dimensions[1]}″ × ${dimensions[0]}″ (Horizontal)`;
        card.parentElement.append(horizontal);
      }
    }
    this.initializeCardImages();
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-portrait-value]');
      if (!button || !this.contains(button)) return;
      details.dataset.portraitUserSelected = 'true';
      const group = button.closest('[data-portrait-option]');
      const input = this.radios(group).find((radio) => radio.value === button.dataset.portraitValue);
      // aria-disabled options remain selectable, exactly as in the native theme,
      // so customers can inspect sold-out combinations and choose another frame/size.
      if (input && button.dataset.portraitOrientation) {
        const orientation = this.orientationRadios().find(radio => radio.value === button.dataset.portraitOrientation);
        for (const target of [input, orientation].filter(Boolean)) {
          if (typeof this.picker.updateSelectedOption === 'function') this.picker.updateSelectedOption(target);
          else target.checked = true;
        }
        const checked = [...this.picker.querySelectorAll('input[data-option-name]:checked')];
        const variant = this.variantMedia.variants.find(variant => this.variantMedia.optionNames.every((name, index) => checked.find(radio => radio.dataset.optionName === name)?.value === variant.options[index]));
        const previous = input.dataset.variantId;
        if (variant) input.dataset.variantId = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        if (previous === undefined) delete input.dataset.variantId; else input.dataset.variantId = previous;
      } else input?.click();
      this.sync();
    }, { signal });
    this.picker.addEventListener('change', event => {
      if (event.isTrusted) details.dataset.portraitUserSelected = 'true';
      this.sync();
    }, { signal });
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.picker, { subtree: true, childList: true, attributes: true });
    this.sync();
    this.configurePresentation();
  }

  initializeCardImages() {
    const names = this.variantMedia.optionNames || [];
    const sizeIndex = names.findIndex(name => portraitNormalize(name) === 'size');
    const orientationIndex = names.findIndex(name => portraitNormalize(name) === 'orientation');
    const frameIndex = names.findIndex(name => /^(color|frame)$/i.test(name));
    for (const card of this.querySelectorAll('[data-portrait-kind="size"] [data-portrait-value]')) {
      const orientation = card.dataset.portraitOrientation || (this.orientation(card.dataset.portraitValue) === 'horizontal' ? 'Horizontal' : 'Vertical');
      const variant = this.variantMedia.variants?.find(item => item.options[sizeIndex] === card.dataset.portraitValue && (orientationIndex < 0 || item.options[orientationIndex] === orientation) && (frameIndex < 0 || item.options[frameIndex] === 'Black'));
      const dimensions = this.dimensions(card.dataset.portraitValue);
      const largest = dimensions && Math.min(...dimensions) === 24 && Math.max(...dimensions) === 36;
      const fixedVertical=orientation==='Vertical' && this.dataset.verticalCardImage;      const url = fixedVertical || (orientation === 'Horizontal' && largest && this.dataset.horizontalLargeImage ? this.dataset.horizontalLargeImage : variant?.url);
      if (!url) continue;
      let image = card.querySelector('img');
      if (!image) { image = document.createElement('img'); image.className = 'portrait-options__image'; card.querySelector('.portrait-options__placeholder')?.replaceWith(image); }
      image.removeAttribute('srcset'); image.src = url; image.alt = `${card.querySelector('.portrait-options__label').textContent.trim()} — fixed size example`;
      if(fixedVertical){image.alt=`${card.querySelector('.portrait-options__label').textContent.trim()} — Color Full body Stella, white background, name at top; fixed unframed poster example`;image.style.objectFit='contain';image.style.objectPosition='center';}      card.dataset.fixedPreview = 'true';
    }
  }

  dimensions(value) {
    const match = String(value).match(/(\d+(?:\.\d+)?)\s*["″]?\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    return match ? [Number(match[1]), Number(match[2])] : null;
  }

  orientation(value) {
    if (/\bvertical\b/i.test(value)) return 'vertical';
    if (/\bhorizontal\b/i.test(value)) return 'horizontal';
    const dimensions = this.dimensions(value);
    return dimensions && dimensions[0] !== dimensions[1] ? (dimensions[0] < dimensions[1] ? 'vertical' : 'horizontal') : '';
  }

  syncOrientation() {
    const group = this.querySelector('[data-portrait-kind="size"]');
    const selected = group && this.radios(group).find(input => input.checked);
    const nativeOrientation = this.orientationRadios().find(input => input.checked)?.value;
    const value = nativeOrientation ? nativeOrientation.toLowerCase() : this.orientation(selected?.value);
    const property = this.querySelector('[data-orientation-property]');
    if (property) { property.disabled = !value; property.value = value ? value[0].toUpperCase() + value.slice(1) : ''; }
  }

  configurePresentation() {
    const details = this.closest('.personalized-product-details--portrait');
    const frameOrder = ['White', 'Natural', 'Espresso', 'Black'];
    for (const group of this.querySelectorAll('[data-portrait-kind]')) {
      const cards = [...group.querySelectorAll('[data-portrait-value]')];
      const size = group.dataset.portraitKind === 'size';
      const rank = card => {
        if (!size) { const index = frameOrder.indexOf(card.dataset.portraitValue); return index < 0 ? 99 : index; }
        const dimensions = this.dimensions(card.dataset.portraitValue);
        return dimensions ? (card.dataset.portraitOrientation === 'Horizontal' || dimensions[0] > dimensions[1] ? 0 : 100000) + dimensions[0] * dimensions[1] : 200000;
      };
      cards.sort((a, b) => rank(a) - rank(b)).forEach(card => card.parentElement.append(card));
    }
    this.numberSteps();
    // Liquid exposes selected_variant only for a valid explicit Shopify selection.
    // Resolve both option values before one native change request. Other options (e.g. Depth) stay selected.
    if (this.dataset.defaultSize === 'true' && this.dataset.explicitVariant !== 'true' && this.dataset.defaultVariantId && !details.dataset.portraitDefaultApplied) {
      let defaultOptions;
      try { defaultOptions = JSON.parse(this.dataset.defaultOptions || '[]'); } catch { return; }
      const group = this.querySelector('[data-portrait-kind="size"]');
      const radio = group && this.radios(group).find(input => defaultOptions.includes(input.value) && !input.disabled);
      const frameGroup = this.querySelector('[data-portrait-kind="frame"]');
      const black = frameGroup && this.radios(frameGroup).find(input => portraitNormalize(input.value) === 'black' && !input.disabled);
      if (!radio || (frameGroup && !black)) return;
      const targets = [...this.picker.querySelectorAll('input[data-option-name]')].filter(input => defaultOptions.includes(input.value) && !input.disabled);
      customElements.whenDefined('variant-picker').then(() => {
        if (!this.isConnected || details.dataset.portraitUserSelected || !radio.isConnected) return;
        details.dataset.portraitDefaultApplied = 'true';
        if (targets.every(input => input.checked)) return;
        for (const input of targets) {
          // The change handler updates the dispatching radio itself. Updating it
          // twice corrupts the native picker's current/previous option markers.
          if (input === radio || input.checked) continue;
          if (typeof this.picker.updateSelectedOption === 'function') this.picker.updateSelectedOption(input);
          else input.checked = true;
        }
        radio.checked = true;
        // Native URL handling otherwise reads the radio's old single-option variant association.
        const previousVariantId = radio.dataset.variantId;
        radio.dataset.variantId = this.dataset.defaultVariantId;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        if (previousVariantId === undefined) delete radio.dataset.variantId;
        else radio.dataset.variantId = previousVariantId;
        this.sync();
      });
    }
  }

  numberSteps() {
    const details = this.closest('.personalized-product-details--portrait');
    const offset = details.querySelector('[data-portrait-step="style"]') ? 1 : 0;
    const framed = !!this.querySelector('[data-portrait-kind="frame"]:not([hidden])');
    const defaults = { style: 'Choose your portrait style', size: 'Choose your portrait size', frame: 'Choose your frame color', background: 'Choose your portrait background color', names: 'Personalize your pet names', upload: 'Upload your pet photo(s)' };
    const numbers = { style: 1, size: 1 + offset, frame: 2 + offset, background: (framed ? 3 : 2) + offset, names: (framed ? 4 : 3) + offset, upload: (framed ? 5 : 4) + offset };
    for (const [key, fallback] of Object.entries(defaults)) {
      const heading = details.querySelector('[data-portrait-step="' + key + '"]');
      if (!heading) continue;
      const label = heading.textContent.replace(/^\s*\d+\.\s*/, '').trim() || fallback;
      const text = numbers[key] + '. ' + label;
      if (heading.textContent !== text) heading.textContent = text;
    }
  }

  radios(group) {
    return Array.from(this.picker.querySelectorAll('input[type="radio"][data-option-name]'))
      .filter((input) => portraitNormalize(input.dataset.optionName) === portraitNormalize(group.dataset.portraitOption));
  }

  sync() {
    const nextGroups = new Set();
    for (const group of this.querySelectorAll('[data-portrait-option]')) {
      const radios = this.radios(group);
      const buttons = Array.from(group.querySelectorAll('[data-portrait-value]'));
      const nativeGroup = radios[0]?.closest('.variant-option');
      const ready = this.picker.dataset.productId === this.dataset.productId && nativeGroup &&
        (radios.length === buttons.length || radios.length * 2 === buttons.length && this.orientationRadios().length === 2) && buttons.length > 0 &&
        buttons.every((button) => radios.some((radio) => radio.value === button.dataset.portraitValue));
      group.hidden = !ready;
      if (ready) {
        nextGroups.add(nativeGroup);
        if (!nativeGroup.classList.contains('portrait-options-native')) nativeGroup.classList.add('portrait-options-native');
      }
      for (const button of buttons) {
        const radio = radios.find((input) => input.value === button.dataset.portraitValue);
        button.setAttribute('aria-pressed', String(Boolean(radio?.checked && (!button.dataset.portraitOrientation || this.orientationRadios().some(input => input.checked && input.value === button.dataset.portraitOrientation)))));
        const dimensions = this.dimensions(button.dataset.portraitValue);
        const excluded = group.dataset.portraitKind === 'size' && dimensions && dimensions.slice().sort((a,b)=>a-b).join('x') === '16x20';
        button.hidden = Boolean(excluded);
        button.disabled = !radio || radio.disabled || Boolean(excluded);
        const unavailable = radio?.dataset.optionAvailable === 'false' || radio?.getAttribute('aria-disabled') === 'true';
        button.dataset.available = String(!unavailable);
        button.querySelector('[data-portrait-availability]').hidden = !unavailable;
      }
    }
    for (const radio of this.orientationRadios()) {
      const group = radio.closest('.variant-option');
      if (group) { if (!group.classList.contains('portrait-options-native')) group.classList.add('portrait-options-native'); nextGroups.add(group); }
    }
    for (const group of this.groups) {
      if (!nextGroups.has(group)) group.classList.remove('portrait-options-native');
    }
    this.groups = nextGroups;
    this.syncOrientation();
    this.numberSteps();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    for (const group of this.groups ?? []) group.classList.remove('portrait-options-native');
  }
}

if (!customElements.get('la-portrait-options')) customElements.define('la-portrait-options', PersonalizedPortraitOptions);
